/**
 * OASIS Application — Discriminated Union Types
 *
 * Models the DOJ Attorney General's Honors Program / Summer Law Intern Program
 * unified application.  Conditional paths (Current Law Student vs. Law School
 * Graduate, HP vs. SLIP, etc.) are captured as discriminated unions so that
 * consumers can narrow on a discriminant property.
 *
 * Generated from the 2025 application screenshots and ApplicationSummary PDF.
 * All PII replaced with dummy values.
 */

// ─── Scalar / Enum‑like Types ────────────────────────────────────────────────

export type YesNo = 'yes' | 'no';

// Forward type-only reference to the unified timeline slice (defined in
// oasis-timeline.types.ts, which imports leaf types from this file). Type-only
// imports are erased at compile time, so this cross-reference is safe.
import type { IEducationExperienceTimeline } from './oasis-timeline.types';
import type { IDemographics } from './oasis-demographics.types';

/** How the applicant first learned about DOJ legal‑hiring programs. */
export type HowLearnedSource =
	'oarm_webinar' | 'law_school_career_services' | 'doj_website' | 'job_fair_or_recruitment_event' | 'doj_attorney_or_employee' | 'judge';

/** Top‑level program the applicant is routed into. */
export type ProgramType = 'honors_program' | 'slip';

/** SLIP sub‑track (only when program === 'slip'). */
export type SlipTrack = 'slip_pathways' | 'slip_law_clerk';

/** Applicant's self‑reported status at time of application. */
export type ApplicantStatusType = 'current_law_student' | 'law_school_graduate';

// ── Citizenship ──────────────────────────────────────────────────────────────

/**
 * Statutory eligibility basis for non-U.S.-citizen applicants. Mirrors the
 * criteria in the AC (8 U.S.C. citations). `none` means the applicant selected
 * "None of the criteria apply to me" — a hard stop: the Citizenship section
 * blocks Save & Continue while it is selected.
 *
 * `us_officer_or_employee_2009` is a point-in-time fact, not a current job: the
 * question asks whether the applicant was a U.S. government officer or employee
 * ON December 16, 2009. (It replaced a present-tense "is an officer or employee"
 * option, which is why the code carries the year.)
 */
export type NonCitizenEligibility =
	| 'lpr_seeking_citizenship'
	| 'refugee_declared_intent'
	| 'asylee_declared_intent'
	| 'us_officer_or_employee_2009'
	| 'permanent_allegiance'
	| 'none';

/**
 * Citizenship section (2026 AC).
 *
 * Question-driven: U.S. citizen yes/no; dual-citizenship (only meaningful for
 * U.S. citizens, so only captured then); dual country; and, for non-citizens,
 * the statutory eligibility basis plus the country of citizenship.
 */
export interface CitizenshipInfo {
	/** "Are you a U.S. citizen?" */
	isUsCitizen: YesNo;
	/** "Are you a dual citizen...?" — only captured when `isUsCitizen` is 'yes'. */
	isDualCitizen?: YesNo;
	/** The other country of citizenship — only when `isDualCitizen` is 'yes'. */
	dualCitizenshipCountry?: string;
	/** Non-citizen statutory eligibility basis — only when `isUsCitizen` is 'no'. */
	nonCitizenEligibility?: NonCitizenEligibility;
	/**
	 * A non-citizen's country of citizenship. Captured only when they meet one of
	 * the eligibility criteria (i.e. `nonCitizenEligibility` is set and not
	 * 'none'). Distinct from {@link dualCitizenshipCountry}, which belongs to the
	 * U.S.-citizen branch and means "the OTHER country".
	 */
	citizenshipCountry?: string;
}

// ── Education ────────────────────────────────────────────────────────────────

export type DegreeType = 'JD' | 'LLM' | 'SJD' | 'BA' | 'BS' | 'MA' | 'MS' | 'MBA' | 'PhD' | 'other';

export type GraduationHonors = 'summa_cum_laude' | 'magna_cum_laude' | 'cum_laude' | 'with_distinction' | 'none';

/**
 * Legal Education (2026 AC redesign)
 *
 * The applicant answers a J.D. question, which routes them down one of three
 * degree paths (U.S. J.D. / Non-U.S. J.D. / Non-J.D.). The student-vs-graduate
 * framing of the question text is driven by `programEligibility.applicantStatus`
 * and does not change the stored shape. Transfer students record the school that
 * granted the degree here, and the school(s) they left as `additionalLawSchools`.
 */

/** The degree path an applicant follows for their initial law degree. */
export type LawDegreePath = 'us_jd' | 'non_us_jd' | 'non_jd';

/**
 * A single attendance span at a law school. Still used by the graduate-legal
 * (LLM) block, which can have more than one span.
 *
 * NOTE: the initial legal degree no longer uses this. The conferral date is a
 * property of the DEGREE, not of each school, so it lives on
 * `InitialLegalDegreeEntry.lawDegreeExpectedOrConferred`, and any earlier law
 * schools are captured as {@link IAdditionalLawSchool}.
 */
export interface ILawSchoolAttendance {
	startDate: string;
	endDate: string;
	/** "Date Law Degree Expected / Conferred". */
	lawDegreeExpectedOrConferred: string;
}

/**
 * A law school the applicant attended that did NOT confer their law degree —
 * typically the school a transfer student started at.
 *
 * Deliberately minimal: only a name and the dates attended. The degree-granting
 * school gets the full treatment (ABA dropdown / non-U.S. fields, grades, bar
 * jurisdiction) because that is the school OARM evaluates; asking for more
 * detail about a school the applicant left than about the one granting the
 * degree would be backwards.
 */
export interface IAdditionalLawSchool {
	schoolName: string;
	startDate: string;
	endDate: string;
}

/** U.S. J.D. degree-conferring school (selected from the ABA dropdown). */
export interface IUsJdLawSchool {
	path: 'us_jd';
	/** Dropdown selection; equals the "not listed" sentinel when unlisted. */
	schoolSelection: string;
	/** The following are populated only when the school is not in the dropdown. */
	schoolName?: string;
	city?: string;
	stateTerritory?: string;
}

/** Non-U.S. J.D. degree-conferring school (manual entry). */
export interface INonUsJdLawSchool {
	path: 'non_us_jd';
	schoolName: string;
	city: string;
	country: string;
}

/**
 * Non-J.D. legal degree used to establish U.S. bar eligibility. Applicants on
 * this path are flagged for OARM review (see `OarmReviewFlag`).
 */
export interface INonJdLawSchool {
	path: 'non_jd';
	schoolName: string;
	city: string;
	country: string;
	/** Free text, entered without abbreviation. */
	degreeType: string;
	/**
	 * U.S. jurisdiction that permits sitting for the bar based solely on this
	 * degree. One of `US_BAR_JURISDICTIONS` (includes `Unknown` and `None`).
	 */
	barJurisdiction: string;
}

export type DegreeConferringLawSchool = IUsJdLawSchool | INonUsJdLawSchool | INonJdLawSchool;

/** "Degree Conferring Law School Grades & Class Standing". */
export interface ILawSchoolGrades {
	/** GPA as entered (string to accommodate non-U.S. scales). */
	gpa: string;
	/** Grade range as entered (free text). */
	gradeRange: string;
	classRank: ClassRank;
}

// ── Graduate Level Legal Education (LLM) sub-block ────────────────────────────

/** Best-fit status describing a graduate-level legal program. */
export type GraduateLegalStatus =
	| 'joint_jd_llm_current_student'
	| 'graduate_llm_after_jd'
	| 'graduate_llm_after_non_jd'
	| 'graduate_non_llm_after_initial'
	| 'graduate_part_time_llm_after_initial'
	| 'none_of_the_above';

/** U.S. LLM law school (selected from the ABA dropdown). */
export interface IUsLlmLawSchool {
	path: 'us';
	schoolSelection: string;
	schoolName?: string;
	city?: string;
	stateTerritory?: string;
}

/** Non-U.S. LLM law school (manual entry). */
export interface INonUsLlmLawSchool {
	path: 'non_us';
	schoolName: string;
	city: string;
	country: string;
	/** Joint US / non-US LLM degree program recognized by the U.S. law school. */
	jointDegreeRecognized: boolean;
}

export type LlmLawSchool = IUsLlmLawSchool | INonUsLlmLawSchool;

export interface IGraduateLegalEducation {
	/** "Have you completed or are you currently enrolled in an LLM program?" */
	hasLlm: YesNo;
	school?: LlmLawSchool;
	/** Up to 2 attendance spans (sufficient for most cases). */
	attendance: ILawSchoolAttendance[];
	statusSelection?: GraduateLegalStatus;
}

/**
 * Law School Class Rank. App-wide source of truth for class rank, consumed by
 * the Education & Experience timeline's legal-degree grades block.
 */
export type ClassRank =
	'top_5_percent' | 'top_10_percent' | 'top_20_percent' | 'top_third' | 'top_half' | 'lower_half' | 'does_not_rank' | 'partially_ranks_not_ranked';

// ── Bar Admission ────────────────────────────────────────────────────────────

export type BarAdmissionStatus = 'admitted' | 'admission_pending' | 'not_admitted';

export interface IBarAdmitted {
	status: 'admitted';
	/** One or more U.S. jurisdictions where the applicant is admitted to the Bar. */
	jurisdictions: string[];
	dateAdmitted: string;
}

export interface IBarPending {
	status: 'admission_pending';
	/** One or more U.S. jurisdictions where the applicant has taken the Bar Exam and awaits results. */
	jurisdictions: string[];
	expectedDate: string;
}

export interface IBarNotAdmitted {
	status: 'not_admitted';
}

export type BarAdmission = IBarAdmitted | IBarPending | IBarNotAdmitted;

// ── Eligibility‑Preserving Activities ────────────────────────────────────────

/** Work schedule shared by EPA entries. */
export type EpaWorkSchedule = 'full_time' | 'part_time';

/** Paid / unpaid, shared by EPA entries. */
export type EpaPayment = 'paid' | 'unpaid';

/**
 * Court-type category for a judicial clerkship. The specific court (circuit,
 * district, or named court) is captured in `courtName`.
 */
export type ClerkshipCourtType =
	'us_supreme_court' | 'federal_circuit_court' | 'us_district_court' | 'other_federal_court' | 'state_or_tribal_court' | 'other_court';

/** Job title for a judicial clerkship. */
export type ClerkshipJobTitle = 'judicial_law_clerk' | 'staff_attorney' | 'other';

/** Top-level fellowship category. */
export type FellowshipType = 'doj_fellowship' | 'non_doj_honors_program' | 'non_profit_public_interest' | 'other_fellowship';

/**
 * Specific DOJ Honors Program fellowship or term appointment (when
 * `fellowshipType` = doj_fellowship). The `*_term_honors` values cover
 * two-to-three-year Honors Program term appointments; applicants name the
 * specific office in Duties & Responsibilities.
 */
export type DojFellowshipType =
	| 'antitrust_aag'
	| 'criminal_asset_forfeiture'
	| 'criminal_nsd_cyber'
	| 'gaye_tenoso_indian_country'
	| 'eoir_term_honors'
	| 'usao_eousa_term_honors'
	| 'other_term_honors';

/*
 * RETIRED (Sept 2026): `IClerkship`, `IFellowship`, `IGraduateLawDegree` and the
 * `EligibilityPreservingActivity` union that combined them, along with the
 * `postGradActivities` arrays that carried them on both eligibility branches.
 *
 * They were the pre-timeline model for eligibility-preserving activities, discriminated
 * on `type`. The consolidated Education & Experience timeline replaced them with
 * `ClerkshipEntry` / `FellowshipEntry` (discriminated on `kind`, stored under
 * `educationExperience.entries`) and shipped BEFORE production release, so no stored
 * application ever carried the old shape. Nothing had written one since; the only
 * remaining code was a read-and-write-back passthrough on the Program Eligibility screen
 * and a display projector kept for bodies that turned out not to exist.
 *
 * The enums above — `ClerkshipCourtType`, `ClerkshipJobTitle`, `FellowshipType`,
 * `DojFellowshipType`, and `EpaWorkSchedule` / `EpaPayment` further up — are still LIVE:
 * the timeline entries use all six. They stay.
 */

// ── Current‑Law‑Student vs. Law‑School‑Graduate Eligibility ─────────────────

/**
 * Where the applicant is in law school, per OARM's 2026 revision.
 *
 * These options deliberately encode more than a year: two of them also state
 * part-time status and whether the applicant graduates this academic year. That
 * overlaps `lawSchoolEnrollmentStatus` (full/part time) and the J.D. date
 * collected alongside it, and the two can disagree — implemented as specified,
 * with no cross-field reconciliation, at the client's direction.
 */
export type LawStudentYear =
	| '1L_first_semester'
	| '1L_second_semester'
	| '2L_or_3L_part_time_not_graduating'
	| '3L_graduating'
	| '4L_or_later_part_time_graduating'
	| 'none_of_the_above';

/** Display labels for {@link LawStudentYear}, in the order OARM lists them. */
export const LAW_STUDENT_YEAR_LABELS: Record<LawStudentYear, string> = {
	'1L_first_semester': 'First Year - First Semester',
	'1L_second_semester': 'First Year - Second Semester',
	'2L_or_3L_part_time_not_graduating': 'Second Year or third-year part-time law student not graduating this academic year',
	'3L_graduating': 'Third year graduating this academic year',
	'4L_or_later_part_time_graduating': 'Fourth Year or later part-time student graduating this academic year',
	none_of_the_above: 'None of the above.',
};

export interface IJointDegreeInfo {
	isJointDegreeStudent: true;
	secondDegreeType: string;
	secondDegreeSchool: string;
	secondDegreeExpectedCompletion: string;
	hasCompletedJD: YesNo;
	hasCompletedSecondDegree: YesNo;
}

export interface INotJointDegree {
	isJointDegreeStudent: false;
}

export type JointDegreeStatus = IJointDegreeInfo | INotJointDegree;

export interface ICurrentLawStudentEligibility {
	applicantStatus: 'current_law_student';
	lawSchoolEnrollmentStatus: 'full_time' | 'part_time';
	currentYear: LawStudentYear;
	expectedJDDate: string;
	jointDegree: JointDegreeStatus;
}

export interface ILawSchoolGraduateEligibility {
	applicantStatus: 'law_school_graduate';
	jdCompletionDate: string;
}

export type ProgramEligibility = ICurrentLawStudentEligibility | ILawSchoolGraduateEligibility;

// ── Professional Accomplishments ─────────────────────────────────────────────

export interface IAccomplishmentItem {
	selected: boolean;
	/** Free‑text details (max 500 characters). Only required when selected. */
	description?: string;
}

export interface IResearchAndWriting {
	lawReviewEditorialPosition: IAccomplishmentItem;
	primaryLawReviewMember: IAccomplishmentItem;
	otherLawReviewMember: IAccomplishmentItem;
	researchAssistant: IAccomplishmentItem;
	fullTimeJournalist: IAccomplishmentItem;
	compensatedSpeechWriting: IAccomplishmentItem;
	collegeNewspaperEditor: IAccomplishmentItem;
	/** Relocated from the eliminated "Other Experience" section per OARM (2026). */
	fullTimeParalegal: IAccomplishmentItem;
}

export interface IOralAdvocacy {
	mootCourtAwardWinner: IAccomplishmentItem;
	mootCourtParticipant: IAccomplishmentItem;
	mootCourtSelectee: IAccomplishmentItem;
	mockTrialAwardWinner: IAccomplishmentItem;
	mockTrialMember: IAccomplishmentItem;
	mockTrialSelectee: IAccomplishmentItem;
	collegeDebate: IAccomplishmentItem;
	publicSpeakingClub: IAccomplishmentItem;
}

export interface IPublicService {
	congressionalInternship: IAccomplishmentItem;
	dojLegalInternship: IAccomplishmentItem;
	federalLegalInternship: IAccomplishmentItem;
	federalNonLegalInternship: IAccomplishmentItem;
	federalJudicialInternship: IAccomplishmentItem;
	fullTimeFederalStateEmployee: IAccomplishmentItem;
	intergovernmentalOrgInternship: IAccomplishmentItem;
	stateLocalGovtLegalInternship: IAccomplishmentItem;
	vitaTaxPreparer: IAccomplishmentItem;
	legalNonProfitVolunteer: IAccomplishmentItem;
	stateJudicialInternship: IAccomplishmentItem;
	nonLegalNonProfitEmployment: IAccomplishmentItem;
	nonLegalVolunteerism: IAccomplishmentItem;
	peaceCorpsAmericorps: IAccomplishmentItem;
	proBonoWork: IAccomplishmentItem;
	/** Relocated from Honors & Awards per OARM (2026). */
	lawSchoolStudentBarOfficer: IAccomplishmentItem;
	/** Relocated from the eliminated "Subject Matter Experience" section per OARM (2026). */
	correctionsLawEnforcementOfficer: IAccomplishmentItem;
}

export interface ILitigationExperience {
	lawSchoolPracticalExperience: IAccomplishmentItem;
	litigationRelatedWork: IAccomplishmentItem;
}

export interface IProfessionalAccomplishments {
	researchAndWriting: IResearchAndWriting;
	oralAdvocacy: IOralAdvocacy;
	publicService: IPublicService;
	litigationExperience: ILitigationExperience;
}

// ── Honors & Awards ──────────────────────────────────────────────────────────

export interface IHonorsAndAwards {
	lawSchoolDeansList: IAccomplishmentItem;
	awardForLegalWriting: IAccomplishmentItem;
	awardOralAdvocacyFirstYear: IAccomplishmentItem;
	meritBasedLawScholarship: IAccomplishmentItem;
	highestGradeAward: IAccomplishmentItem;
	prestigiousLawSchoolHonors: IAccomplishmentItem;
	prestigiousGradScholarship: IAccomplishmentItem;
	orderOfTheCoif: IAccomplishmentItem;
	phiBetaKappa: IAccomplishmentItem;
	collegeSummaCumLaude: IAccomplishmentItem;
	collegeMagnaCumLaude: IAccomplishmentItem;
	collegeCumLaude: IAccomplishmentItem;
	lawSchoolSummaCumLaude: IAccomplishmentItem;
	lawSchoolMagnaCumLaude: IAccomplishmentItem;
	lawSchoolCumLaude: IAccomplishmentItem;
}

// ── Employment ───────────────────────────────────────────────────────────────

export type PaymentType = 'paid' | 'unpaid' | 'volunteer';

export type WorkSchedule = 'full_time' | 'part_time';

export interface IEmploymentEntry {
	jobTitle: string;
	employer: string;
	city: string;
	stateOrCountry: string;
	/**
	 * Paid / unpaid / volunteer, or `''` when not yet answered.
	 *
	 * This used to be non-optional and defaulted to `'paid'` on every new row, so an
	 * applicant who never touched the control still asserted "Paid" — and OARM reads it
	 * as a badge on the detail page with no way to tell a real answer from the default.
	 * `''` makes "not answered" representable, and the select opens on a placeholder.
	 *
	 * `X | ''` rather than optional, matching `courtType`, `jobTitle` and
	 * `fellowshipType` on the timeline entries, which are the established idiom here for
	 * an enum the applicant has not chosen yet.
	 *
	 * Deliberately still NOT required. Employment rows are optional in full, so nothing
	 * on that screen is validated; making this the one mandatory field would be
	 * arbitrary. See TODO.md.
	 */
	payment: PaymentType | '';
	workSchedule: WorkSchedule;
	fromDate: string;
	toDate: string;
	supervisorName: string;
	supervisorTitle: string;
	supervisorPhone: string;
	dutiesResponsibilities: string;
	/**
	 * Set when this entry is derived from an Eligibility Preserving Activity
	 * (clerkship/fellowship). Such rows are rendered read-only on the Employment
	 * screen; they are edited only on the Eligibility Activities screen.
	 */
	readonly sourceActivity?: 'clerkship' | 'fellowship';
}

export interface IEmployment {
	dojExperience: IEmploymentEntry[];
	otherExperience: IEmploymentEntry[];
}

// ── References ───────────────────────────────────────────────────────────────

export interface IReference {
	name: string;
	relationToApplicant: string;
	title: string;
	company: string;
	cityState: string;
	phone: string;
	email: string;
}

// ── Military Service & Veterans' Preference ──────────────────────────────────

export type MilitaryBranch = 'army' | 'navy' | 'air_force' | 'marine_corps' | 'coast_guard' | 'space_force' | 'national_guard';

export type MilitaryDischargeType =
	'honorable' | 'general_under_honorable' | 'other_than_honorable' | 'bad_conduct' | 'dishonorable' | 'not_applicable';

export type ReferenceRelationship = 'professor' | 'employer' | 'supervisor' | 'colleague' | 'mentor' | 'other';

export type MilitaryServiceStatus = 'served' | 'derivative_eligible' | 'no_response_not_applicable';

export type ServiceCharacterization = 'honorable_or_general' | 'other';

/**
 * Veterans' preference category, tracked by alphabetic code per OARM:
 *  SSP = Sole Survivorship, TP = 5-point, XP/CP/CPS = 10-point variants.
 */
export type VeteranPreferenceCategory = 'ssp' | 'tp' | 'xp' | 'cp' | 'cps';

/** One period of active-duty service (applicants may list several). */
export interface IServicePeriod {
	branch: MilitaryBranch;
	from: string;
	to: string;
}

export interface IMilitaryServiceServed {
	serviceStatus: 'served';
	servicePeriods: IServicePeriod[];
	characterization: ServiceCharacterization;
	isEligibleForPreference: YesNo;
	preferenceCategory?: VeteranPreferenceCategory;
}

export interface IMilitaryDerivativeEligible {
	serviceStatus: 'derivative_eligible';
	isEligibleForPreference: YesNo;
	preferenceCategory?: VeteranPreferenceCategory;
}

export interface IMilitaryNotApplicable {
	serviceStatus: 'no_response_not_applicable';
}

export type MilitaryService = IMilitaryServiceServed | IMilitaryDerivativeEligible | IMilitaryNotApplicable;

// ── Essays ───────────────────────────────────────────────────────────────────

export interface IEssays {
	/** Main essay: "Tell us why you want to work for DOJ…" (max 6000 chars). */
	mainEssay: string;
	/** Revised per May 29 OMB Memo — additional essay questions (if applicable). */
	revisedOmbEssays?: {
		constitution: string;
		efficiency: string;
		priorities: string;
		workEthic: string;
		aiCertification: YesNo;
	};
}

// ── Languages ────────────────────────────────────────────────────────────────

export type LanguageProficiency = 'no_proficiency' | 'limited' | 'general_professional' | 'advanced_professional' | 'bilingual';

export interface ILanguageEntry {
	language: string;
	oralProficiency: LanguageProficiency;
	writtenProficiency: LanguageProficiency;
	listenProficiency: LanguageProficiency;
}

// ── Supporting Documents ─────────────────────────────────────────────────────

/**
 * Allowed document categories for supporting-document uploads. Mirrored by the
 * server upload contract (`OasisDocType`). Aligned to the Supporting Documents
 * AC (2026): law transcript, fellowship/offer letter, DD-214, disability
 * preference (CPS/CP/XP), and derivative preference.
 */
export type OasisDocType = 'law_transcript' | 'fellowship_offer_letter' | 'dd214' | 'disability_preference' | 'derivative_preference';

/**
 * A supporting document actually uploaded to S3. Structurally matches the
 * server's `OasisUploadRef`; the file bytes live in S3 and only this reference
 * is persisted on the application body.
 */
export interface ISupportingDocument {
	s3Key: string;
	docType: OasisDocType;
	filename: string;
	contentType: string;
	sizeBytes: number;
	uploadedAt: string;
	/** Optional applicant-supplied note shown in the Description column. */
	description?: string;
}

// ── Components / Offices of Interest ─────────────────────────────────────────

/**
 * A single hiring-office selection, stored as a SNAPSHOT taken at the time of
 * application. The hiring-office catalog is cycle-specific and rebuilt each
 * year (closed cycles are never re-run), so we freeze the display fields here
 * rather than relying on a later lookup against an evolving catalog. `code` is
 * the stable join key for reporting/admin.
 */
export interface IComponentSelection {
	/** Stable catalog code, e.g. "HP-CIV-APPELLATE", "SPW-CIV-COMLIT". */
	code: string;
	/** Office/position label as displayed to the applicant (no hire count). */
	label: string;
	/** Program the selection belongs to. */
	program: ProgramType;
	/** SLIP sub-track, when program === 'slip'. */
	track?: SlipTrack;
	/** HP participation type (formal vs informal). Absent for SLIP. */
	participation?: 'formal' | 'informal';
	/** Anticipated hires as displayed (a string to allow ranges like "2-5"). */
	anticipatedHires: string;
}

export interface IComponentsOfInterest {
	/** The offices/positions the applicant selected (snapshotted). */
	selections: IComponentSelection[];
}

// ── Certify & Submit ─────────────────────────────────────────────────────────

export interface ICertifyAndSubmit {
	/** Required. Consent to DOJ notifying/consulting/disclosing to authorities re: discrepancies. */
	consentDisclosure: YesNo;
	/** Required. Post-submission: cannot modify (except contact) without withdraw + recertify. */
	understandPostSubmissionRules: YesNo;
	/** Required. No simultaneous HP/SLIP consideration; post-deadline clerkship transfer path. */
	noSimultaneousConsideration: YesNo;
	/** Required. Final certification that the application is true and correct. */
	certifyTrueAndCorrect: YesNo;
}

// ── Acknowledgements ─────────────────────────────────────────────────────────

/**
 * Required applicant acknowledgements on the Instructions screen. All must be
 * affirmed ('yes') to proceed. Keys map 1:1 to the seven acknowledgement
 * statements in the Instructions & Acknowledgements AC.
 */
export interface IAcknowledgements {
	/** 1. Reviewed and understand the instructions above. */
	reviewedInformation: YesNo;
	/** 2. May be considered by applied-to components and other hiring offices. */
	consideredByComponents: YesNo;
	/** 3. No late submissions; the system enforces the deadline. */
	understandNoLateSubmissions: YesNo;
	/** 4. An acceptable law school transcript is required or the applicant is disqualified. */
	transcriptRequired: YesNo;
	/** 5. Must remain continuously eligible throughout the hiring cycle. */
	continuousEligibility: YesNo;
	/** 6. Understands DOJ does not defer entry on duty absent extraordinary circumstances. */
	understandNoDeferral: YesNo;
	/** 7. Certifies the application is accurate and complete. */
	certifyAccurate: YesNo;
}

// ── Application Metadata ─────────────────────────────────────────────────────

export type ApplicationStatus = 'not_started' | 'in_progress' | 'certified_submitted' | 'withdrawn' | 'transferred';

/**
 * OARM review flags.
 *
 * A flat, typed list of reasons an application needs manual review by the
 * Office of Attorney Recruitment & Management. Kept as a single top-level
 * array (rather than booleans scattered across sections) so downstream logic
 * can test membership in one place — e.g. `reviewFlags.includes(...)` or
 * `reviewFlags.length > 0`. Add new reasons as new union members.
 */
export type OarmReviewFlag =
	| 'legal_education_non_jd_degree'
	| 'legal_education_joint_degree_current_student'
	| 'other_education_joint_degree'
	| 'epa_clerkship_under_12_months'
	| 'epa_clerkship_part_time'
	| 'epa_clerkship_other_court'
	| 'epa_fellowship_under_12_months'
	| 'epa_fellowship_part_time'
	| 'epa_fellowship_non_doj_hp'
	| 'epa_fellowship_non_profit'
	| 'epa_fellowship_other'
	| 'epa_fellowship_law_firm_sponsor'
	/** Initial law degree was earned outside the U.S. (non-U.S. J.D. path). */
	| 'legal_education_non_us_degree'
	/**
	 * More than 9 months between the end of law education (J.D. conferral, or the
	 * later of the J.D. / second degree for joint-degree applicants) and the start
	 * of the applicant's first eligibility-preserving activity.
	 */
	| 'legal_education_epa_gap'
	/**
	 * A graduate law degree (LLM) that COUNTS as an EPA ran outside one academic
	 * year — under 9 months or over 12 months. Joint-degree, concurrent, and
	 * part-time LLMs are not EPAs and are never flagged for duration.
	 */
	| 'epa_llm_duration'
	/** More than 120 days between two consecutive eligibility-preserving activities. */
	| 'epa_gap'
	/**
	 * DISPLAY-ONLY tag: the applicant claimed veterans' preference (Military
	 * Service). Surfaced to OARM in the flags column/filter but does NOT route
	 * the application to `flag_review`.
	 */
	| 'veterans_preference_claimed';

/**
 * Human-readable labels for each OARM review flag, used by the OARM landing
 * flags column + filter. Keyed by the union so adding a flag forces a label.
 */
export const OARM_REVIEW_FLAG_LABELS: Record<OarmReviewFlag, string> = {
	legal_education_non_jd_degree: 'Non-J.D. Legal Degree',
	legal_education_joint_degree_current_student: 'Joint Degree (Current Student)',
	other_education_joint_degree: 'Joint Degree (Other Education)',
	epa_clerkship_under_12_months: 'Clerkship < 12 Months',
	epa_clerkship_part_time: 'Part-Time Clerkship',
	epa_clerkship_other_court: 'Clerkship — Other Court',
	epa_fellowship_under_12_months: 'Fellowship < 12 Months',
	epa_fellowship_part_time: 'Part-Time Fellowship',
	epa_fellowship_non_doj_hp: 'Non-DOJ Honors Fellowship',
	epa_fellowship_non_profit: 'Non-Profit Fellowship',
	epa_fellowship_other: 'Other Fellowship',
	epa_fellowship_law_firm_sponsor: 'Law-Firm-Sponsored Fellowship',
	legal_education_non_us_degree: 'Non-U.S. Law Degree',
	legal_education_epa_gap: 'Gap: Law Degree to First EPA (> 9 Months)',
	epa_llm_duration: 'Graduate Law Degree Duration (Outside 9–12 Months)',
	epa_gap: 'Gap Between EPAs (> 120 Days)',
	veterans_preference_claimed: "Veterans' Preference",
};

/**
 * Review flags that are DISPLAY-ONLY (informational): they surface to OARM but
 * are NOT counted as review flags and never gate `flag_review`. Mirrors the
 * backend `DISPLAY_ONLY_REVIEW_FLAGS`.
 */
export const INFORMATIONAL_REVIEW_FLAGS: readonly OarmReviewFlag[] = ['veterans_preference_claimed'];

/** Human-readable labels for veterans' preference categories (SSP/TP/XP/CP/CPS). */
export const VETERAN_PREFERENCE_CATEGORY_LABELS: Record<VeteranPreferenceCategory, string> = {
	ssp: 'SSP — Sole Survivorship',
	tp: 'TP — 5-Point',
	xp: 'XP — 10-Point (Other)',
	cp: 'CP — 10-Point Compensable Disability',
	cps: 'CPS — 10-Point Compensable Disability (30%+)',
};

export interface IApplicationMetadata {
	applicationId: string;
	cycleYear: number;
	submissionTimestamp?: string;
	lastModified: string;
	applicationStatus: ApplicationStatus;
	ombNumber: string;
	ombExpirationDate: string;
	/**
	 * Applicant's in-form progress, persisted on every draft save so the form can
	 * resume where they left off and the nav guard can gate access to sections
	 * they haven't reached yet. This is a UX convenience only — it is NEVER
	 * authoritative for the workflow (the server header `status` is). Absent on
	 * bodies written before progress tracking, and on fully-submitted apps that
	 * never draft-saved.
	 */
	progress?: {
		/** Section keys the applicant has completed (see APPLICATION_SECTIONS). */
		completedSections: string[];
		/** The section key the applicant last viewed — a resume hint. */
		lastVisitedSection?: string;
		/**
		 * Sections whose collected answers were DISCARDED because the applicant
		 * changed something they depended on (see `SUBSTANTIVE_CHANGES`) — for
		 * example switching program, which invalidates Components of Interest
		 * because the component catalog differs per program.
		 *
		 * Persisted so the "this was cleared, and why" notice survives a reload and
		 * so the nav can tell a section that needs re-answering apart from one that
		 * is merely awaiting review. A key drops out as soon as that section is
		 * saved again.
		 */
		clearedSections?: string[];
	};
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT APPLICATION — Discriminated Union Version
// ═══════════════════════════════════════════════════════════════════════════════

export interface IOasisApplicationBase {
	metadata: IApplicationMetadata;
	instructionsAndAcknowledgements: {
		howLearnedAboutProgram: HowLearnedSource;
		acknowledgements: IAcknowledgements;
	};
	personalInformation: {
		firstName: string;
		middleName?: string;
		lastName: string;
		currentAddress: string;
		aptSuite?: string;
		city: string;
		stateProvince: string;
		otherState?: string;
		country: string;
		zipPostalCode: string;
		phoneNumber: string;
		emailAddress: string;
		confirmEmailAddress: string;
	};
	citizenship: CitizenshipInfo;
	componentsOfInterest: IComponentsOfInterest;
	barAdmission: BarAdmission;
	/**
	 * Unified Education & Experience timeline (2026) — the single source of truth
	 * for legal education, other education, and eligibility-preserving activities.
	 * Replaced the former `legalEducation`, `otherEducation`, and
	 * `eligibilityActivities` fields, which were retired once the timeline passed
	 * UAT. The Employment screen renders derived clerkship/fellowship entries
	 * read-only from this slice.
	 */
	educationExperience: IEducationExperienceTimeline;
	professionalAccomplishments: IProfessionalAccomplishments;
	honorsAndAwards: IHonorsAndAwards;
	employment: IEmployment;
	references: IReference[];
	militaryService: MilitaryService;
	essays: IEssays;
	languages: ILanguageEntry[];
	additionalInformation?: string;
	/**
	 * Voluntary self-identification for aggregate equal-opportunity reporting.
	 * Optional and access-restricted: this block is collected only for OARM's
	 * aggregate reporting and must not be returned to reviewers/hiring officials.
	 * Absent when the applicant never reached or fully opted out of the section.
	 */
	demographics?: IDemographics;
	supportingDocuments: ISupportingDocument[];
	certifyAndSubmit: ICertifyAndSubmit;
	/**
	 * Aggregated OARM review flags raised across sections. Populated at submit
	 * time from per-section flag logic. Empty/absent means no manual review
	 * was triggered.
	 */
	reviewFlags?: OarmReviewFlag[];
}

/**
 * Honors Program applicant (routed as HP).  The `programEligibility`
 * discriminant narrows to the correct eligibility sub‑type.
 */
export interface IOasisHonorsProgramApplication extends IOasisApplicationBase {
	program: 'honors_program';
	programEligibility: ProgramEligibility;
}

/**
 * SLIP Pathways applicant.
 */
export interface IOasisSlipPathwaysApplication extends IOasisApplicationBase {
	program: 'slip';
	slipTrack: 'slip_pathways';
	programEligibility: ICurrentLawStudentEligibility;
}

/**
 * SLIP‑Law Clerk applicant.
 */
export interface IOasisSlipLawClerkApplication extends IOasisApplicationBase {
	program: 'slip';
	slipTrack: 'slip_law_clerk';
	programEligibility: ICurrentLawStudentEligibility;
}

/**
 * The top‑level discriminated union.  Consumers narrow on `program`
 * (and, if SLIP, on `slipTrack`) to access the correct eligibility shape.
 */
export type OasisApplication = IOasisHonorsProgramApplication | IOasisSlipPathwaysApplication | IOasisSlipLawClerkApplication;
